import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addMovement,
  closeDrawer,
  getCurrentDrawer,
  listDrawerHistory,
  openDrawer,
  type CurrentDrawerDTO,
} from './cash-drawer.api';
import { formatCop } from '../../lib/format';
import { readApiError } from '../../lib/api';

export function CashDrawerPage() {
  const qc = useQueryClient();
  const current = useQuery({
    queryKey: ['cash-drawer', 'current'],
    queryFn: getCurrentDrawer,
    refetchInterval: 30_000,
  });
  const history = useQuery({
    queryKey: ['cash-drawer', 'history'],
    queryFn: listDrawerHistory,
  });
  const [error, setError] = useState<string | null>(null);

  const openMut = useMutation({
    mutationFn: openDrawer,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cash-drawer'] }),
    onError: (e) => setError(readApiError(e).message),
  });

  const movMut = useMutation({
    mutationFn: (vars: { drawerId: number; kind: 'CASH_IN' | 'CASH_OUT'; amount: number; reason: string }) =>
      addMovement(vars.drawerId, { kind: vars.kind, amount: vars.amount, reason: vars.reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cash-drawer'] }),
    onError: (e) => setError(readApiError(e).message),
  });

  const closeMut = useMutation({
    mutationFn: (vars: { drawerId: number; countedCash: number; notes?: string }) =>
      closeDrawer(vars.drawerId, { countedCash: vars.countedCash, notes: vars.notes }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cash-drawer'] }),
    onError: (e) => setError(readApiError(e).message),
  });

  return (
    <section>
      <h1 style={{ marginTop: 0 }}>Arqueo de Caja</h1>
      {error && <p className="error">{error}</p>}

      {current.isLoading && <p>Cargando...</p>}
      {current.data === null && <OpenDrawerCard onSubmit={(v) => openMut.mutate(v)} submitting={openMut.isPending} />}
      {current.data && (
        <CurrentDrawerCard
          drawer={current.data}
          onMovement={(b) => movMut.mutate({ drawerId: current.data!.id, ...b })}
          onClose={(b) => closeMut.mutate({ drawerId: current.data!.id, ...b })}
          submittingMov={movMut.isPending}
          submittingClose={closeMut.isPending}
        />
      )}

      {history.data && history.data.length > 0 && (
        <div className="card" style={{ marginTop: '1.5rem', padding: 0, overflow: 'hidden' }}>
          <h3 style={{ margin: 0, padding: '0.75rem 1rem', background: 'var(--color-bg)' }}>
            Historial de cierres
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: 'var(--color-bg)' }}>
                <th style={{ padding: '0.5rem' }}>Apertura</th>
                <th style={{ padding: '0.5rem' }}>Cierre</th>
                <th style={{ padding: '0.5rem' }}>Cajero</th>
                <th style={{ padding: '0.5rem', textAlign: 'right' }}>Float</th>
                <th style={{ padding: '0.5rem', textAlign: 'right' }}>Esperado</th>
                <th style={{ padding: '0.5rem', textAlign: 'right' }}>Contado</th>
                <th style={{ padding: '0.5rem', textAlign: 'right' }}>Diferencia</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {history.data.map((d) => {
                const diff = d.difference !== null ? Number(d.difference) : 0;
                return (
                  <tr key={d.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '0.5rem' }}>{new Date(d.openedAt).toLocaleString('es-CO')}</td>
                    <td style={{ padding: '0.5rem' }}>
                      {d.closedAt ? new Date(d.closedAt).toLocaleString('es-CO') : '—'}
                    </td>
                    <td style={{ padding: '0.5rem' }}>{d.openedByName}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatCop(d.openingFloat)}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                      {d.expectedCash !== null ? formatCop(d.expectedCash) : '—'}
                    </td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                      {d.countedCash !== null ? formatCop(d.countedCash) : '—'}
                    </td>
                    <td
                      style={{
                        padding: '0.5rem',
                        textAlign: 'right',
                        color: Math.abs(diff) < 0.01 ? 'var(--color-accent)' : 'var(--color-danger)',
                      }}
                    >
                      {d.difference !== null ? formatCop(diff) : '—'}
                    </td>
                    <td style={{ padding: '0.5rem' }}>
                      <a
                        className="btn"
                        href={`/api/cash-drawer/${d.id}/pdf`}
                        target="_blank"
                        rel="noreferrer"
                        style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                      >
                        PDF
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function OpenDrawerCard(props: { onSubmit: (openingFloat: number) => void; submitting: boolean }) {
  const [floatStr, setFloatStr] = useState('0');
  return (
    <form
      className="card"
      style={{ maxWidth: 480 }}
      onSubmit={(e) => {
        e.preventDefault();
        props.onSubmit(Number(floatStr) || 0);
      }}
    >
      <h3 style={{ marginTop: 0 }}>Sin caja abierta</h3>
      <p style={{ color: 'var(--color-muted)' }}>
        Abre tu caja para empezar el turno. Ingresa el efectivo inicial (float).
      </p>
      <div className="field">
        <label>Float inicial</label>
        <input
          className="input"
          type="number"
          min={0}
          step="0.01"
          value={floatStr}
          onChange={(e) => setFloatStr(e.target.value)}
          autoFocus
        />
      </div>
      <button className="btn btn-primary" disabled={props.submitting}>
        {props.submitting ? 'Abriendo...' : 'Abrir caja'}
      </button>
    </form>
  );
}

function CurrentDrawerCard(props: {
  drawer: CurrentDrawerDTO;
  onMovement: (b: { kind: 'CASH_IN' | 'CASH_OUT'; amount: number; reason: string }) => void;
  onClose: (b: { countedCash: number; notes?: string }) => void;
  submittingMov: boolean;
  submittingClose: boolean;
}) {
  const s = props.drawer.summary;
  const [movKind, setMovKind] = useState<'CASH_IN' | 'CASH_OUT'>('CASH_OUT');
  const [movAmount, setMovAmount] = useState('');
  const [movReason, setMovReason] = useState('');
  const [showClose, setShowClose] = useState(false);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '1rem' }}>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Resumen del turno</h3>
        <p style={{ color: 'var(--color-muted)', marginTop: 0 }}>
          Abierta {new Date(props.drawer.openedAt).toLocaleString('es-CO')} ·{' '}
          {s.invoiceCount} factura(s) registradas
        </p>

        <table style={{ width: '100%', fontSize: '0.95rem', marginTop: '0.5rem' }}>
          <tbody>
            <tr>
              <td>Float inicial</td>
              <td style={{ textAlign: 'right' }}>{formatCop(s.openingFloat)}</td>
            </tr>
            <tr>
              <td>+ Ventas en efectivo</td>
              <td style={{ textAlign: 'right' }}>{formatCop(s.cashSales)}</td>
            </tr>
            <tr>
              <td>+ Ingresos manuales</td>
              <td style={{ textAlign: 'right' }}>{formatCop(s.cashIn)}</td>
            </tr>
            <tr>
              <td>- Egresos manuales</td>
              <td style={{ textAlign: 'right' }}>-{formatCop(s.cashOut)}</td>
            </tr>
            <tr style={{ borderTop: '1px solid var(--color-border)', fontWeight: 700 }}>
              <td style={{ paddingTop: '0.5rem' }}>Efectivo esperado</td>
              <td style={{ textAlign: 'right', paddingTop: '0.5rem', fontSize: '1.2rem' }}>
                {formatCop(s.expected)}
              </td>
            </tr>
          </tbody>
        </table>

        {s.paymentBreakdown.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: 'var(--color-muted)' }}>
              Pagos por método
            </div>
            <ul style={{ margin: '0.25rem 0', paddingLeft: '1.2rem' }}>
              {s.paymentBreakdown.map((b) => (
                <li key={b.method}>
                  {b.method} ({b.count}): {formatCop(b.amount)}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div style={{ marginTop: '1.5rem' }}>
          <button className="btn btn-primary" onClick={() => setShowClose(true)}>
            Cerrar caja
          </button>
        </div>

        {showClose && (
          <CloseDrawerModal
            expected={s.expected}
            onCancel={() => setShowClose(false)}
            onSubmit={(b) => {
              props.onClose(b);
              setShowClose(false);
            }}
            submitting={props.submittingClose}
          />
        )}
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Ingresos / Egresos manuales</h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            props.onMovement({
              kind: movKind,
              amount: Number(movAmount) || 0,
              reason: movReason.trim(),
            });
            setMovAmount('');
            setMovReason('');
          }}
        >
          <div className="field">
            <label>Tipo</label>
            <select className="input" value={movKind} onChange={(e) => setMovKind(e.target.value as 'CASH_IN' | 'CASH_OUT')}>
              <option value="CASH_OUT">Egreso (CASH_OUT)</option>
              <option value="CASH_IN">Ingreso (CASH_IN)</option>
            </select>
          </div>
          <div className="field">
            <label>Monto</label>
            <input
              className="input"
              type="number"
              min={0.01}
              step="0.01"
              value={movAmount}
              onChange={(e) => setMovAmount(e.target.value)}
              required
            />
          </div>
          <div className="field">
            <label>Razón</label>
            <input
              className="input"
              value={movReason}
              onChange={(e) => setMovReason(e.target.value)}
              required
              placeholder="Ej. Cambio de $50.000, gastos menores..."
            />
          </div>
          <button
            className="btn btn-primary"
            disabled={props.submittingMov || !movAmount || !movReason}
          >
            Registrar
          </button>
        </form>

        {props.drawer.movements.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: 'var(--color-muted)' }}>
              Movimientos del turno
            </div>
            <ul style={{ margin: '0.25rem 0', paddingLeft: '1.2rem', fontSize: '0.85rem' }}>
              {props.drawer.movements.map((m) => (
                <li key={m.id}>
                  <span style={{ color: m.kind === 'CASH_IN' ? 'var(--color-accent)' : 'var(--color-danger)' }}>
                    {m.kind === 'CASH_IN' ? '+' : '-'} {formatCop(m.amount)}
                  </span>{' '}
                  {m.reason}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function CloseDrawerModal(props: {
  expected: number;
  onCancel: () => void;
  onSubmit: (b: { countedCash: number; notes?: string }) => void;
  submitting: boolean;
}) {
  const [counted, setCounted] = useState('');
  const [notes, setNotes] = useState('');
  const numericCounted = Number(counted) || 0;
  const diff = numericCounted - props.expected;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
      }}
    >
      <form
        className="card"
        style={{ width: 460 }}
        onSubmit={(e) => {
          e.preventDefault();
          props.onSubmit({ countedCash: numericCounted, notes: notes || undefined });
        }}
      >
        <h3 style={{ marginTop: 0 }}>Cerrar caja</h3>
        <p>
          Esperado: <strong>{formatCop(props.expected)}</strong>
        </p>
        <div className="field">
          <label>Efectivo contado</label>
          <input
            className="input"
            type="number"
            min={0}
            step="0.01"
            value={counted}
            onChange={(e) => setCounted(e.target.value)}
            autoFocus
            required
          />
        </div>
        {counted && (
          <p style={{ color: Math.abs(diff) < 0.01 ? 'var(--color-accent)' : 'var(--color-danger)' }}>
            Diferencia: {formatCop(diff)}
            {Math.abs(diff) >= 0.01 && (diff > 0 ? ' (sobrante)' : ' (faltante)')}
          </p>
        )}
        <div className="field">
          <label>Notas (opcional)</label>
          <textarea
            className="input"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Justifica diferencias o anota observaciones del turno"
          />
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="submit" className="btn btn-primary" disabled={props.submitting || !counted}>
            {props.submitting ? 'Cerrando...' : 'Confirmar cierre'}
          </button>
          <button type="button" className="btn" onClick={props.onCancel}>
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}
